import { Component } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { rem } from 'polished'
import type { WrappedComponentProps } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'

import partnershipRewardMessage from 'lib/partnership/partnershipRewardMessage'

import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { addGTMEvent } from 'api/googleTagManager'
import { promotionBanner } from 'analytics/promotions'

const PartnershipBannerPane = styled(Pane)`
  width: 100%;
  padding: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const BannerContent = styled(PaneBody)`
  display: flex;
  align-items: center;
  justify-content: center;

  > * + * {
    margin-left: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    > * + * {
      margin-left: ${rem(16)};
    }
  }
`

const StyledImage = styled(ResponsiveImage)`
  max-width: ${rem(135)};
`

interface OwnProps {
  bannerId: string
}

interface StateProps {
  partnership: App.Partnership | undefined
}

type Props = OwnProps & StateProps & WrappedComponentProps

class PartnershipBannerContainer extends Component<React.PropsWithChildren<Props>> {
  trackContentImpression = () => {
    addGTMEvent(promotionBanner({ bannerId: this.props.bannerId, hitType: 'promotion-impression' }))
  }

  onClickTrack = () => {
    addGTMEvent(promotionBanner({ bannerId: this.props.bannerId, hitType: 'promotion-click' }))
  }

  render() {
    const {
      partnership,
      intl,
    } = this.props

    if (!partnership) {
      return null
    }

    const rewardMessage = partnershipRewardMessage(partnership, intl)
    const linkValue = (chunks: React.ReactNode) => (
      <TextLink onClick={this.onClickTrack} to={`/${partnership.landingPage}`}>
        {chunks}
      </TextLink>
    )

    const burnBannerContent = <FormattedMessage
      id="DJE-TSuJVFx-URvaNqZB"
      defaultMessage="Earn {rewardMessage} OR use your {rewardName} to put towards your next escape. <link>Learn more</link>"
      description="Partnership banner text for earning and using reward points"
      values={{
        rewardMessage,
        rewardName: partnership.rewardName,
        link: linkValue,
      }}
    />

    const earnBannerContent = <FormattedMessage
      id="hjfhE6ZDrPet6SkX6IYG"
      defaultMessage="Earn {rewardMessage}. Link your {programName} account to earn {rewardName} on eligible bookings. <link>Learn more</link>"
      description="Partnership banner text for linking account to earn rewards"
      values={{
        rewardMessage,
        programName: partnership.programName,
        rewardName: partnership.rewardName,
        link: linkValue,
      }}
    />

    return <PartnershipBannerPane>
      <BannerContent size="md-y">
        <StyledImage
          loading="lazy"
          mobileWidth="100px"
          tabletWidth="135px"
          format="png"
          id={partnership.programLogo.id}
          alt={intl.formatMessage({
            id: '1-TELwd-GCeVrJgS1Gyn',
            defaultMessage: '{programName} Logo',
            description: 'Accessibility alt text for partnership program logo',
          }, { programName: partnership.programName })}
          onLoad={this.trackContentImpression}
        />
        <BodyText variant="medium">
          {partnership.hasBurn ? burnBannerContent : earnBannerContent}
        </BodyText>
      </BannerContent>
    </PartnershipBannerPane>
  }
}

const mapStateToProps = (state: App.State): StateProps => ({
  partnership: state.geo.partnership,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(injectIntl(PartnershipBannerContainer))
